import {
  START_ACCOUNT_DELETION_JOB_CALLABLE,
  type StartAccountDeletionJobResponse,
} from '@cip/core/accountDeletion';
import {ProfileProvider} from 'contexts/ProfileContext';
import {useTranslateFunction} from 'contexts/translation/TranslationContext';
import Swal from 'sweetalert2';
import {GenericBrandedWrapper} from './Login';
import {FirebaseError} from 'firebase/app';
import {getAuth, signOut} from 'firebase/auth';
import {getFunctions, httpsCallable} from 'firebase/functions';
import {isNullOrUndefined} from 'utils/general';
import {
  DELETE_CONFIRMATION_WORD,
  getDeleteConfirmationPhrase,
  isDeleteConfirmationMatch,
} from './deleteAccountConfirm';

const functions = getFunctions();

/**
 * Requests deletion of the caller's account (isolated `account-deletion`
 * codebase). The account is locked out at once — Auth disabled, refresh
 * tokens revoked — while the erasure itself runs in the next scheduled
 * deletion run rather than on request; see the `@cip/core/accountDeletion`
 * README, issue #3263, and #3459 for the batching.
 */
const startAccountDeletion = httpsCallable<
  Record<string, never>,
  StartAccountDeletionJobResponse
>(functions, START_ACCOUNT_DELETION_JOB_CALLABLE);

export const DeleteAccount = () => {
  return (
    <ProfileProvider>
      <GenericBrandedWrapper>
        <DeleteAccountInner />
      </GenericBrandedWrapper>
    </ProfileProvider>
  );
};

/**
 * Formats the scheduled deletion run for display, in the viewer's locale.
 *
 * @param scheduledForMs - Epoch millis from the callable response, or null
 *   when the server supplied none (an admin run-now request, or a client
 *   talking to a deployment that predates the field).
 * @return The formatted date, or null when there is nothing to show — in
 *   which case callers must render the date-free fallback copy rather than
 *   guessing a date.
 * @throws Never — see the return contract.
 */
function formatScheduledRun(scheduledForMs: number | null): string | null {
  if (isNullOrUndefined(scheduledForMs) || !Number.isFinite(scheduledForMs)) {
    return null;
  }
  // Rendered in UTC so every viewer sees one day, not one shifted across
  // the date line. The server's wave hour (`computeNextDeletionWaveAt`)
  // falls on the same UTC date as its wave day, and so does the midnight
  // UTC that older tombstones were stamped with.
  return new Date(scheduledForMs).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

const DeleteAccountInner = () => {
  const t = useTranslateFunction('miscUI');

  const deleteMyAccount = async () => {
    const accountEmail = getAuth().currentUser?.email;
    const phrase = getDeleteConfirmationPhrase(accountEmail);
    const hasEmail = phrase !== DELETE_CONFIRMATION_WORD;
    const result = await Swal.fire<StartAccountDeletionJobResponse>({
      title: t('Are you sure?', {forceString: true}),
      text: t(
        'This deletes your account on both codeinplace.stanford.edu and ' +
          'pai.stanford.edu. You will be signed out immediately and will ' +
          'not be able to sign in again. Your data is then erased in our ' +
          'next scheduled deletion run. This cannot be undone.',
        {forceString: true},
      ),
      icon: 'warning',
      input: hasEmail ? 'email' : 'text',
      inputLabel: hasEmail
        ? t('To confirm, type the email on this account: {{email}}', {
            forceString: true,
            substitutions: {email: phrase},
          })
        : t(
            'This account has no email address. To confirm, type the ' +
              'word {{word}}.',
            {forceString: true, substitutions: {word: phrase}},
          ),
      inputAttributes: {autocomplete: 'off', autocapitalize: 'off'},
      customClass: {inputLabel: 'px-4 text-center'},
      inputValidator: (typed: string) => {
        if (isDeleteConfirmationMatch({typed, accountEmail})) {
          return null;
        }
        return hasEmail
          ? t("That doesn't match the email on this account.", {
              forceString: true,
            })
          : t('Type {{word}} to confirm.', {
              forceString: true,
              substitutions: {word: phrase},
            });
      },
      showCancelButton: true,
      confirmButtonText: t('Yes, delete my account', {forceString: true}),
      cancelButtonText: t('No, keep my account', {forceString: true}),
      showLoaderOnConfirm: true,
      // Once the callable is in flight the server may already have disabled
      // the account, so the dialog must stay up to show the outcome.
      allowOutsideClick: () => !Swal.isLoading(),
      allowEscapeKey: () => !Swal.isLoading(),
      preConfirm: async () => {
        try {
          return (await startAccountDeletion({})).data;
        } catch (error) {
          console.error('[DeleteAccount] request failed', error);
          // The validation message renders as HTML, so only the server's own
          // precondition sentence (a legal hold) is shown verbatim.
          Swal.showValidationMessage(
            error instanceof FirebaseError &&
              error.code === 'functions/failed-precondition'
              ? error.message
              : t('An error occurred while deleting your account.', {
                  forceString: true,
                }),
          );
          return false;
        }
      },
    });
    if (!result.isConfirmed) {
      return;
    }

    // The webapp compiles with `strict: false`, so the response type shows
    // `scheduledForMs` as a plain number even though the server sends null
    // for an immediate run. Guarded by hand, per docs/AGENTS.md.
    const runDate = formatScheduledRun(result.value.scheduledForMs);
    // The server has already disabled the account and revoked refresh
    // tokens; signing out clears this device's session state too.
    await signOut(getAuth()).catch(error => {
      console.error('[DeleteAccount] signOut failed', error);
    });
    // This dialog is the only confirmation the user sees: signing out
    // unmounts the page behind it.
    const outcome = isNullOrUndefined(runDate)
      ? t(
          'You have been signed out and your account is now locked. ' +
            'Your data will be erased in our next scheduled deletion ' +
            'run.',
          {forceString: true},
        )
      : t(
          'You have been signed out and your account is now locked. ' +
            'Your data will be erased in our next scheduled deletion ' +
            'run, starting on {{date}}.',
          {forceString: true, substitutions: {date: runDate}},
        );
    const contact = t('If you have any questions, contact {{email}}.', {
      forceString: true,
      substitutions: {email: 'codeinplace@stanford.edu'},
    });
    await Swal.fire({
      title: t('Account deletion scheduled', {forceString: true}),
      text: `${outcome} ${contact}`,
      icon: 'success',
    });
  };

  return (
    <div className="">
      <h1>{t('Delete Account')}</h1>
      <p className="alert alert-danger">
        {t(
          'You use the same account on codeinplace.stanford.edu and ' +
            'pai.stanford.edu, so deleting it deletes it on both sites. ' +
            'Deleting your account signs you out immediately and ' +
            'permanently removes your profile, projects, and other ' +
            'personal data from Code in Place. Posts, messages and ' +
            'section feedback you wrote are emptied rather than removed ' +
            'outright: they are left showing a placeholder, with your ' +
            'name off them, so conversations other people took part in ' +
            'still make sense and the section ratings you gave still ' +
            'count. This action cannot be undone.',
        )}
      </p>
      <button className="btn btn-danger" onClick={deleteMyAccount}>
        {t('Delete My Account')}
      </button>
    </div>
  );
};
